import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { API_BASE } from "@/lib/api";
import { Logo, ServiceBar } from "@/components/Logo";
import { DeliveryServicePoliciesContent } from "@/components/DeliveryServicePolicies";
import { ArrowLeft, FileText } from "lucide-react";

const LOGO_URL = "https://customer-assets-7cd3h4nn.emergentagent.net/job_power-energy-pro/artifacts/bcgoc9pi_upscalemedia-transformed.webp";

/**
 * Página pública `/politicas` — sección 9.9 PRD.
 * Antes vivía embebida en la landing; el cliente pidió moverla a su propio URL
 * (patrón "Términos y condiciones") con enlace desde el footer.
 */
export default function Policies() {
  const [content, setContent] = useState(null);

  useEffect(() => {
    document.title = "Políticas de entrega y servicio — Power & Energy";
    axios.get(`${API_BASE}/public/landing`)
      .then((r) => setContent(r.data))
      .catch(() => setContent(null));
  }, []);

  const brand = content?.brand || { logo_url: LOGO_URL, slogan: "SOLUCIONES DE VANGUARDIA",
    service_bar: "SUBESTACIONES · PROYECTOS · OBRA CIVIL · GESTORÍA · UVIES · INSTALACIONES · MANTENIMIENTO" };

  return (
    <div className="min-h-screen bg-white text-[#0A1118]" data-testid="policies-page">
      {/* Top bar corporativa (mismo patrón que la landing pero sin CTA) */}
      <header className="sticky top-0 z-40 bg-white/95 backdrop-blur border-b border-[#005A91]/10">
        <div className="max-w-4xl mx-auto px-4 lg:px-6 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3" data-testid="policies-back-home">
            <img src={brand.logo_url || LOGO_URL} alt="Power & Energy" className="h-10 w-auto" />
          </Link>
          <Link to="/" data-testid="policies-back-link"
                className="inline-flex items-center gap-1.5 text-sm font-bold text-[#005A91] hover:underline">
            <ArrowLeft className="h-3.5 w-3.5" /> Volver al sitio
          </Link>
        </div>
      </header>

      {/* Encabezado del documento */}
      <div className="border-b border-slate-200">
        <div className="max-w-4xl mx-auto px-4 lg:px-6 py-10 lg:py-14">
          <div className="text-[11px] font-black tracking-[0.24em] uppercase text-[#005A91] flex items-center gap-2">
            <FileText className="h-3.5 w-3.5" /> Documento oficial
          </div>
          <h1 className="text-4xl lg:text-5xl font-black tracking-tight mt-3">
            Políticas de entrega y servicio
          </h1>
          <p className="text-slate-600 mt-4 max-w-2xl leading-relaxed">
            Las reglas del juego con las que operamos cada proyecto —
            para que tú y nosotros sepamos exactamente qué esperar antes, durante
            y después del trabajo. Estas políticas también aparecen al final de
            cada cotización que emitimos.
          </p>
        </div>
      </div>

      {/* Contenido */}
      <main className="max-w-4xl mx-auto px-4 lg:px-6 py-12">
        <DeliveryServicePoliciesContent
          warranties={content?.warranties}
          formal={content?.formal}
          variant="landing"
        />
        <div className="mt-10 pt-6 border-t border-slate-200 text-xs text-slate-500 flex items-center justify-between flex-wrap gap-2">
          <span>Última actualización: {new Date().toLocaleDateString("es-MX", { year: "numeric", month: "long" })}</span>
          <Link to="/" className="text-[#005A91] font-bold hover:underline" data-testid="policies-back-link-2">
            Volver al sitio
          </Link>
        </div>
      </main>

      <ServiceBar text={brand.service_bar} />
      <footer className="bg-slate-900 text-slate-300 py-8">
        <div className="max-w-4xl mx-auto px-4 lg:px-6 flex items-start justify-between flex-wrap gap-6">
          <div>
            <img src={brand.logo_url || LOGO_URL} alt="Power & Energy" className="h-12 w-auto" style={{ filter: "brightness(0) invert(1)" }} />
            <div className="text-[10px] font-black tracking-[0.24em] uppercase text-[#4ECCC8] mt-3">
              {brand.slogan}
            </div>
            <div className="text-xs text-slate-500 mt-2">
              © {new Date().getFullYear()} POWER & ENERGY. Todos los derechos reservados.
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
